import type { Question } from '../../../types/api';

/**
 * Filters out excluded questions and their subQuestions
 * @param fields - Array of questions to filter
 * @returns Filtered array with only non-excluded questions
 */
export const filterExcludedQuestions = (fields: Question[]): Question[] => {
  return fields
    .filter(field => !field.excluded) // Filter out excluded main questions
    .map(field => {
      // If the field has subQuestions, filter them too
      if (field.subQuestions && field.subQuestions.length > 0) {
        return {
          ...field,
          subQuestions: field.subQuestions.filter(subQ => !subQ.excluded),
        };
      }
      return field;
    });
};

// Helper function to group fields into rows based on colSpan
export const groupFieldsIntoRows = (fields: Question[]): Question[][] => {
  const rows: Question[][] = [];
  let currentRow: Question[] = [];
  let currentRowWidth = 0;

  fields.forEach(field => {
    const fieldWidth = field.colSpan || 1;

    // If adding this field would exceed 2 columns, start a new row
    if (currentRowWidth + fieldWidth > 2) {
      if (currentRow.length > 0) {
        rows.push(currentRow);
        currentRow = [];
        currentRowWidth = 0;
      }
    }

    currentRow.push(field);
    currentRowWidth += fieldWidth;

    // If this field spans 2 columns, it fills the entire row
    if (fieldWidth === 2) {
      rows.push(currentRow);
      currentRow = [];
      currentRowWidth = 0;
    }
  });

  // Add the last row if it has fields
  if (currentRow.length > 0) {
    rows.push(currentRow);
  }

  return rows;
};

// Special layout for hotel location forms with map
export const groupFieldsForHotelLocation = (
  fields: Question[]
): {
  locationFields: Question[];
  mapField: Question | null;
  remainingFields: Question[];
} => {
  // Find the map component field
  const mapFieldIndex = fields.findIndex(
    field => field.type === 'custom' && field.component === 'map-with-marker'
  );

  const mapField = mapFieldIndex !== -1 ? fields[mapFieldIndex] : null;

  // Get first 5 non-map fields for location info
  const nonMapFields = fields.filter(
    field => !(field.type === 'custom' && field.component === 'map-with-marker')
  );

  const locationFields = nonMapFields.slice(0, 5);
  const remainingFields = nonMapFields.slice(5);

  return {
    locationFields,
    mapField,
    remainingFields,
  };
};
